"use client";
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import * as React from "react";
import { EASING_FUNCTIONS, KEY_CODES, cj, debounce, extractElement, isServer, useLayoutEffect, useResizeObserver } from "../utils";
import { Link, Container } from "./Basic";
const BREAKPOINTS = {
    medium: 991,
    small: 767,
    tiny: 479
};
function getLinksList(root) {
    return root.querySelectorAll(".w-nav-menu .w-nav-link");
}
export const NavbarContext = /*#__PURE__*/ React.createContext({
    animDirect: 1,
    animOver: false,
    animation: "animation",
    collapse: "medium",
    docHeight: false,
    duration: 400,
    easing2: "ease",
    easing: "ease",
    getBodyHeight: ()=>undefined,
    getOverlayHeight: ()=>{
        return undefined;
    },
    isOpen: false,
    noScroll: false,
    toggleOpen: ()=>undefined,
    navbarMounted: false,
    menu: undefined,
    root: undefined,
    setFocusedLink: ()=>undefined
});
function getAnimationKeyframes({ axis = "Y", start, end }) {
    const t = `translate${axis}`;
    return [
        {
            transform: `${t}(${start}px)`
        },
        {
            transform: `${t}(${end}px)`
        }
    ];
}
export const NavbarWrapper = /*#__PURE__*/ React.forwardRef(function NavbarWrapper(props, ref) {
    const { animation, docHeight, easing, easing2, duration, noScroll } = props.config;
    const root = React.useRef(null);
    const menu = React.useRef(null);
    const animOver = /^over/.test(animation);
    const animDirect = /left$/.test(animation) ? -1 : 1;
    const [focusedLink, setFocusedLink] = React.useState(-1);
    React.useImperativeHandle(ref, ()=>root.current);
    const getBodyHeight = React.useCallback(()=>{
        if (isServer) return;
        return docHeight ? document.documentElement.scrollHeight : document.body.scrollHeight;
    }, [
        docHeight
    ]);
    const getOverlayHeight = React.useCallback(()=>{
        if (isServer || !root.current) return;
        let height = getBodyHeight();
        if (!height) return;
        const style = getComputedStyle(root.current);
        if (!animOver && style.position !== "fixed") {
            height -= root.current.offsetHeight;
        }
        return height;
    }, [
        animOver,
        getBodyHeight
    ]);
    const getOffsetHeight = React.useCallback(()=>{
        if (!root.current || !menu.current) return 0;
        return root.current.offsetHeight + menu.current.offsetHeight;
    }, []);
    const [isOpen, setIsOpen] = React.useState(false);
    const toggleOpen = debounce(()=>{
        if (!menu.current) return;
        if (isOpen) {
            const keyframes = animOver ? getAnimationKeyframes({
                axis: "X",
                start: 0,
                end: animDirect * menu.current.offsetWidth
            }) : getAnimationKeyframes({
                start: 0,
                end: -getOffsetHeight()
            });
            var _EASING_FUNCTIONS_easing2;
            const anim = menu.current.animate(keyframes, {
                easing: (_EASING_FUNCTIONS_easing2 = EASING_FUNCTIONS[easing2]) !== null && _EASING_FUNCTIONS_easing2 !== void 0 ? _EASING_FUNCTIONS_easing2 : "ease",
                duration,
                fill: "forwards"
            });
            anim.onfinish = ()=>{
                setIsOpen(!isOpen);
            };
            return;
        }
        setFocusedLink(-1);
        setIsOpen(!isOpen);
    });
    useLayoutEffect(()=>{
        if (!menu.current) return;
        if (isOpen) {
            const keyframes = animOver ? getAnimationKeyframes({
                axis: "X",
                start: animDirect * menu.current.offsetWidth,
                end: 0
            }) : getAnimationKeyframes({
                start: -getOffsetHeight(),
                end: 0
            });
            var _EASING_FUNCTIONS_easing;
            menu.current.animate(keyframes, {
                easing: (_EASING_FUNCTIONS_easing = EASING_FUNCTIONS[easing]) !== null && _EASING_FUNCTIONS_easing !== void 0 ? _EASING_FUNCTIONS_easing : "ease",
                duration,
                fill: "forwards"
            });
        }
    }, [
        animDirect,
        animOver,
        duration,
        easing,
        getBodyHeight,
        getOffsetHeight,
        isOpen
    ]);
    useLayoutEffect(()=>{
        if (isOpen && noScroll) {
            document.body.style.overflowY = "hidden";
        } else {
            document.body.style.overflowY = "";
        }
        return ()=>{
            document.body.style.overflowY = "";
        };
    }, [
        isOpen,
        noScroll
    ]);
    const closeOnResize = React.useCallback(()=>setIsOpen(false), [
        setIsOpen
    ]);
    useResizeObserver(root, closeOnResize, {
        onlyWidth: true
    });
    React.useEffect(()=>{
        if (root.current) {
            var _links_;
            const links = getLinksList(root.current);
            (_links_ = links[focusedLink !== null && focusedLink !== void 0 ? focusedLink : 0]) === null || _links_ === void 0 ? void 0 : _links_.focus();
        }
    }, [
        focusedLink
    ]);
    return /*#__PURE__*/ React.createElement(NavbarContext.Provider, {
        value: _object_spread_props(_object_spread({}, props.config), {
            root,
            menu,
            animOver,
            animDirect,
            getBodyHeight,
            getOverlayHeight,
            isOpen,
            toggleOpen,
            navbarMounted: true,
            setFocusedLink
        })
    }, /*#__PURE__*/ React.createElement(Navbar, _object_spread({}, props)));
});
const maybeExtractChildMenu = (children, isOpen)=>{
    if (!isOpen) return {
        childMenu: null,
        rest: children
    };
    const { extracted, tree } = extractElement(React.Children.toArray(children), NavbarMenu);
    return {
        childMenu: extracted,
        rest: tree
    };
};
function Navbar(_param) {
    var { tag = "div", className = "", children, config } = _param, props = _object_without_properties(_param, [
        "tag",
        "className",
        "children",
        "config"
    ]);
    const { root, collapse, setFocusedLink } = React.useContext(NavbarContext);
    const [shouldExtractMenu, setShouldExtractMenu] = React.useState(true);
    const extractMenuCallback = React.useCallback((entry)=>{
        setShouldExtractMenu(entry.contentRect.width <= BREAKPOINTS[collapse]);
    }, [
        setShouldExtractMenu
    ]);
    const bodyRef = React.useRef(typeof document !== "undefined" ? document.body : null);
    useResizeObserver(bodyRef, extractMenuCallback);
    const { childMenu, rest } = React.useMemo(()=>maybeExtractChildMenu(children, shouldExtractMenu), [
        children,
        shouldExtractMenu
    ]);
    const handleFocus = (e)=>{
        var _document_activeElement;
        const inputFocused = ((_document_activeElement = document.activeElement) === null || _document_activeElement === void 0 ? void 0 : _document_activeElement.tagName.toLowerCase()) === "input";
        const linkList = root.current ? Array.from(getLinksList(root.current)) : [];
        const linkAmount = linkList.length;
        switch(e.key){
            case KEY_CODES.ARROW_LEFT:
            case KEY_CODES.ARROW_UP:
                {
                    if (!inputFocused) {
                        e.preventDefault();
                        setFocusedLink((prev)=>Math.max(prev - 1, 0));
                    }
                    break;
                }
            case KEY_CODES.ARROW_RIGHT:
            case KEY_CODES.ARROW_DOWN:
                {
                    if (!inputFocused) {
                        e.preventDefault();
                        setFocusedLink((prev)=>Math.min(prev + 1, linkAmount - 1));
                    }
                    break;
                }
            case KEY_CODES.HOME:
                {
                    e.preventDefault();
                    setFocusedLink(0);
                    break;
                }
            case KEY_CODES.END:
                {
                    e.preventDefault();
                    setFocusedLink(linkAmount - 1);
                    break;
                }
            case KEY_CODES.TAB:
                {
                    setTimeout(()=>{
                        setFocusedLink(linkList.findIndex((link)=>link === document.activeElement));
                    }, 0);
                    break;
                }
            case KEY_CODES.SPACE:
                {
                    if (!inputFocused) {
                        e.preventDefault();
                    }
                    break;
                }
            default:
                {
                    return;
                }
        }
    };
    return /*#__PURE__*/ React.createElement(tag, _object_spread_props(_object_spread({}, props), {
        className: cj(className, "w-nav"),
        "data-collapse": config.collapse,
        "data-animation": config.animation,
        ref: root,
        onKeyDown: handleFocus
    }), /*#__PURE__*/ React.createElement(React.Fragment, null, rest, /*#__PURE__*/ React.createElement(NavbarOverlay, null, childMenu)));
}
function NavbarOverlay({ children }) {
    const { isOpen, getOverlayHeight, toggleOpen } = React.useContext(NavbarContext);
    const overlayToggleOpen = React.useCallback((e)=>{
        if (e.target === e.currentTarget) {
            toggleOpen();
        }
    }, [
        toggleOpen
    ]);
    const overlayHeight = getOverlayHeight();
    return /*#__PURE__*/ React.createElement("div", {
        className: "w-nav-overlay",
        id: "w-nav-overlay",
        style: {
            display: isOpen ? "block" : "none",
            height: overlayHeight ? overlayHeight : undefined,
            width: isOpen ? "100%" : 0
        },
        onClick: overlayToggleOpen,
        onKeyDown: overlayToggleOpen
    }, children);
}
export const NavbarContainer = /*#__PURE__*/ React.forwardRef(function NavbarContainer(_param, ref) {
    var { children } = _param, props = _object_without_properties(_param, [
        "children"
    ]);
    const innerRef = React.useRef(null);
    const { isOpen } = React.useContext(NavbarContext);
    React.useImperativeHandle(ref, ()=>innerRef.current);
    const updateLinkStyles = React.useCallback((entry)=>{
        const { maxWidth: containerMaxWidth } = getComputedStyle(entry.target);
        document.querySelectorAll(".w-nav-menu>.w-dropdown,.w-nav-menu>.w-nav-link").forEach((node)=>{
            if (!(node instanceof HTMLElement)) return;
            if (!isOpen) {
                node.style.maxWidth = "";
                return;
            }
            const { maxWidth } = getComputedStyle(node);
            node.style.maxWidth = !maxWidth || maxWidth === "none" || maxWidth === containerMaxWidth ? containerMaxWidth : "";
        });
    }, [
        isOpen
    ]);
    useResizeObserver(innerRef, updateLinkStyles);
    return /*#__PURE__*/ React.createElement(Container, _object_spread_props(_object_spread({}, props), {
        ref: innerRef
    }), children);
});
export const NavbarBrand = /*#__PURE__*/ React.forwardRef(function NavbarBrand(_param, ref) {
    var { className = "" } = _param, props = _object_without_properties(_param, [
        "className"
    ]);
    return /*#__PURE__*/ React.createElement(Link, _object_spread_props(_object_spread({}, props), {
        className: cj(className, "w-nav-brand"),
        ref: ref
    }));
});
export const NavbarMenu = /*#__PURE__*/ React.forwardRef(function NavbarMenu(_param, ref) {
    var { tag = "nav", className = "" } = _param, props = _object_without_properties(_param, [
        "tag",
        "className"
    ]);
    const { getBodyHeight, animOver, isOpen, menu } = React.useContext(NavbarContext);
    React.useImperativeHandle(ref, ()=>menu.current);
    return /*#__PURE__*/ React.createElement(tag, _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, props), {
        className: cj(className, "w-nav-menu")
    }), isOpen ? {
        "data-nav-menu-open": ""
    } : {}), {
        style: animOver ? {
            height: getBodyHeight()
        } : {},
        ref: menu
    }));
});
export const NavbarLink = /*#__PURE__*/ React.forwardRef(function NavbarLink(_param, ref) {
    var { className = "" } = _param, props = _object_without_properties(_param, [
        "className"
    ]);
    const { isOpen } = React.useContext(NavbarContext);
    return /*#__PURE__*/ React.createElement(Link, _object_spread_props(_object_spread({}, props), {
        className: cj(className, "w-nav-link", isOpen && "w--nav-link-open"),
        ref: ref
    }));
});
export const NavbarButton = /*#__PURE__*/ React.forwardRef(function NavbarButton(_param, ref) {
    var { tag = "div", className = "" } = _param, props = _object_without_properties(_param, [
        "tag",
        "className"
    ]);
    const { isOpen, toggleOpen } = React.useContext(NavbarContext);
    return /*#__PURE__*/ React.createElement(tag, _object_spread_props(_object_spread({}, props), {
        "aria-label": "menu",
        "aria-expanded": isOpen ? "true" : "false",
        "aria-haspopup": "menu",
        "aria-controls": "w-nav-overlay",
        role: "button",
        tabIndex: 0,
        className: cj(className, "w-nav-button", isOpen && "w--open"),
        onClick: toggleOpen,
        onKeyDown: (e)=>{
            if (e.key === KEY_CODES.ENTER || e.key === KEY_CODES.SPACE) {
                e.preventDefault();
                toggleOpen();
            }
        },
        ref
    }));
});
